import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api/client';
import { useUserContext } from '@/lib/auth/useUserContext';
import { isHrStaff } from '@/lib/auth/rbac';

interface BaristaUser {
  id: string;
  username: string;
  displayName: string | null;
  role: 'barista' | 'shift_lead';
  status: 'active' | 'disabled' | 'terminated';
  homeStoreId: string;
  homeStoreCode: string;
  homeStoreName: string;
  createdAt: string;
  disabledAt: string | null;
  lastLoginAt: string | null;
  mustChangePin: boolean | null;
  lockedUntil: string | null;
  pinExpiresAt: string | null;
}

interface ListResponse {
  users: BaristaUser[];
  nextCursor: string | null;
}

interface ManagerUser {
  id: string;
  email: string;
  displayName: string | null;
  lastSeenAt: string | null;
  entraAdminUrl: string;
}

interface ManagerListResponse {
  tier: 'manager';
  users: ManagerUser[];
}

interface CreateResponse {
  tempPin: string;
  user: {
    id: string;
    username: string;
    role: string;
    homeStoreId: string;
    homeStoreCode: string;
  };
}

interface ResetPinResponse {
  tempPin: string;
  userId: string;
  username: string;
}

interface PinDisclosure {
  username: string;
  storeCode: string;
  tempPin: string;
  kind: 'create' | 'reset';
}

interface BulkOutcome {
  userId: string;
  status: 'ok' | 'skipped';
  reason?: string;
  tempPin?: string;
}

interface BulkResponse {
  operation: 'disable' | 'enable' | 'reset_pin';
  results: BulkOutcome[];
}

// Tier-2 PIN/lifecycle mutations go to core's BFF proxy (/users*), which forwards
// to identity-svc's tier2/* routes. (Reads use core's own GET /users.)
const apiBase = '/users';

/**
 * Tier-1 admin UI for Tier-2 user lifecycle. List, create, reset PIN, disable.
 * Route gate: at least one user-admin-capable group (TMKN-Qargo-Admins / HR /
 * CLevel) OR any managerial group (Mgr-*, Regional-*, District-*). Per-row
 * actions are visible only when the caller's groups + scope permit them.
 *
 * The temp-PIN response (from create and reset-pin) is shown once in a modal
 * with copy + a printable handoff card — architecture §4.4.5.
 */
export function AdminUsers() {
  const { groups, scopedStores, tier } = useUserContext();
  const [activeTier, setActiveTier] = useState<'store_ops' | 'manager'>('store_ops');
  const [rows, setRows] = useState<BaristaUser[]>([]);
  const [managers, setManagers] = useState<ManagerUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'disabled'>('active');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [pinModal, setPinModal] = useState<PinDisclosure | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPins, setBulkPins] = useState<BulkOutcome[] | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const isOrgWide = useMemo(
    () =>
      groups.some(
        (g) => g === 'TMKN-Qargo-Admins' || g === 'TMKN-Qargo-CLevel' || g === 'TMKN-Qargo-HR',
      ),
    [groups],
  );
  // HR staff (HR/Admins/CLevel) can open the HR profile page for any barista.
  const showHrProfile = useMemo(() => isHrStaff(groups), [groups]);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    params.set('tier', activeTier);
    if (activeTier === 'store_ops' && filterStatus !== 'all') params.set('status', filterStatus);
    if (search) params.set('search', search);
    if (activeTier === 'manager') {
      apiFetch<ManagerListResponse>(`/users?${params.toString()}`)
        .then((r) => setManagers(r.users))
        .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
    } else {
      apiFetch<ListResponse>(`/users?${params.toString()}`)
        .then((r) => setRows(r.users))
        .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
        .finally(() => setLoading(false));
    }
  }, [activeTier, filterStatus, search]);

  useEffect(() => {
    if (tier === 'manager') reload();
  }, [tier, reload]);

  async function resetPin(u: BaristaUser) {
    try {
      const r = await apiFetch<ResetPinResponse>(`${apiBase}/${u.id}/reset-pin`, {
        method: 'POST',
      });
      setPinModal({
        username: r.username,
        storeCode: u.homeStoreCode,
        tempPin: r.tempPin,
        kind: 'reset',
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function runBulk(operation: 'disable' | 'enable' | 'reset_pin') {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    setError(null);
    try {
      const r = await apiFetch<BulkResponse>(`${apiBase}/bulk`, {
        method: 'POST',
        body: JSON.stringify({
          userIds: Array.from(selectedIds),
          operation,
        }),
      });
      if (operation === 'reset_pin') {
        setBulkPins(r.results);
      }
      setSelectedIds(new Set());
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBulkBusy(false);
    }
  }

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function toggleDisabled(u: BaristaUser) {
    const nextStatus = u.status === 'disabled' ? 'active' : 'disabled';
    try {
      await apiFetch(`${apiBase}/${u.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function isCallerScopeMatch(storeId: string): boolean {
    return isOrgWide || scopedStores.includes(storeId);
  }

  return (
    <div className="min-h-screen p-6 max-w-6xl mx-auto">
      <header className="flex items-center justify-between border-b border-border pb-4 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-sm text-muted-foreground">
            {isOrgWide ? 'org-wide access' : `${scopedStores.length} stores in scope`}
          </p>
        </div>
        {activeTier === 'store_ops' ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
          >
            Create user
          </button>
        ) : null}
      </header>

      <div className="flex items-center gap-1 mb-4 border-b border-border">
        <button
          type="button"
          onClick={() => setActiveTier('store_ops')}
          className={`px-3 py-2 text-sm border-b-2 -mb-[2px] ${
            activeTier === 'store_ops'
              ? 'border-primary font-medium'
              : 'border-transparent text-muted-foreground'
          }`}
        >
          Tier 2 (baristas + shift leads)
        </button>
        <button
          type="button"
          onClick={() => setActiveTier('manager')}
          className={`px-3 py-2 text-sm border-b-2 -mb-[2px] ${
            activeTier === 'manager'
              ? 'border-primary font-medium'
              : 'border-transparent text-muted-foreground'
          }`}
        >
          Tier 1 (managers)
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        {activeTier === 'store_ops' ? (
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'disabled')}
            className="border border-border rounded-md px-2 py-1 text-sm"
          >
            <option value="all">all</option>
            <option value="active">active</option>
            <option value="disabled">disabled</option>
          </select>
        ) : null}
        <input
          type="search"
          placeholder={
            activeTier === 'manager' ? 'email or display name' : 'username or display name'
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') reload();
          }}
          className="flex-1 border border-border rounded-md px-3 py-1 text-sm"
        />
        <button
          type="button"
          onClick={reload}
          className="px-3 py-1 rounded-md border border-border text-sm"
        >
          Search
        </button>
      </div>

      {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}

      {activeTier === 'manager' ? (
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground border-b border-border">
            <tr>
              <th className="py-2">Email</th>
              <th>Name</th>
              <th>Last seen</th>
              <th className="text-right">Manage</th>
            </tr>
          </thead>
          <tbody>
            {managers.map((m) => (
              <tr key={m.id} className="border-b border-border/40">
                <td className="py-2">
                  <code className="text-xs">{m.email}</code>
                </td>
                <td>{m.displayName ?? '—'}</td>
                <td>
                  {m.lastSeenAt
                    ? new Date(m.lastSeenAt).toISOString().slice(0, 16).replace('T', ' ')
                    : '—'}
                </td>
                <td className="text-right">
                  <a
                    href={m.entraAdminUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline underline-offset-4"
                  >
                    Open in Entra ↗
                  </a>
                </td>
              </tr>
            ))}
            {!loading && managers.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-6 text-center text-muted-foreground">
                  No managers yet — these populate as Entra users sign in.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      ) : (
        <>
          {selectedIds.size > 0 ? (
            <div className="border border-primary/40 bg-primary/5 rounded-md px-3 py-2 flex items-center gap-3 mb-3 text-sm">
              <span>{selectedIds.size} selected</span>
              <button
                type="button"
                onClick={() => void runBulk('disable')}
                disabled={bulkBusy}
                className="px-2 py-1 text-xs underline underline-offset-4 text-amber-700"
              >
                Disable
              </button>
              <button
                type="button"
                onClick={() => void runBulk('enable')}
                disabled={bulkBusy}
                className="px-2 py-1 text-xs underline underline-offset-4 text-emerald-700"
              >
                Enable
              </button>
              <button
                type="button"
                onClick={() => void runBulk('reset_pin')}
                disabled={bulkBusy}
                className="px-2 py-1 text-xs underline underline-offset-4"
              >
                Reset PIN
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="ml-auto text-xs text-muted-foreground"
              >
                Clear selection
              </button>
            </div>
          ) : null}
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr>
                <th className="py-2 w-8"></th>
                <th>Username</th>
                <th>Name</th>
                <th>Store</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last login</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const canActOn = isCallerScopeMatch(u.homeStoreId);
                return (
                  <tr key={u.id} className="border-b border-border/40">
                    <td className="py-2 align-top">
                      {canActOn ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(u.id)}
                          onChange={() => toggleSelected(u.id)}
                        />
                      ) : null}
                    </td>
                    <td className="py-2">
                      {showHrProfile ? (
                        <Link
                          to={`/admin/users/${u.id}`}
                          className="text-xs underline underline-offset-4 hover:text-primary"
                          title="Open HR profile"
                        >
                          <code>{u.username}</code>
                        </Link>
                      ) : (
                        <code className="text-xs">{u.username}</code>
                      )}
                    </td>
                    <td>{u.displayName ?? '—'}</td>
                    <td>
                      {u.homeStoreCode}
                      <span className="text-muted-foreground"> · {u.homeStoreName}</span>
                    </td>
                    <td>{u.role}</td>
                    <td>
                      <span
                        className={
                          u.status === 'active'
                            ? 'text-emerald-700'
                            : u.status === 'disabled'
                              ? 'text-amber-700'
                              : 'text-muted-foreground'
                        }
                      >
                        {u.status}
                      </span>
                      {u.mustChangePin ? (
                        <span className="ml-2 text-xs text-amber-700">must-change</span>
                      ) : null}
                      {u.lockedUntil && new Date(u.lockedUntil) > new Date() ? (
                        <span className="ml-2 text-xs text-destructive">locked</span>
                      ) : null}
                    </td>
                    <td>
                      {u.lastLoginAt
                        ? new Date(u.lastLoginAt).toISOString().slice(0, 16).replace('T', ' ')
                        : '—'}
                    </td>
                    <td className="text-right">
                      {canActOn ? (
                        <>
                          <button
                            type="button"
                            onClick={() => void resetPin(u)}
                            className="text-xs underline underline-offset-4 mr-3"
                          >
                            Reset PIN
                          </button>
                          <button
                            type="button"
                            onClick={() => void toggleDisabled(u)}
                            className="text-xs underline underline-offset-4 text-amber-700"
                          >
                            {u.status === 'disabled' ? 'Enable' : 'Disable'}
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">out of scope</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-muted-foreground">
                    No users.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </>
      )}

      {createOpen ? (
        <CreateUserDialog
          onClose={() => setCreateOpen(false)}
          onCreated={(disclosure) => {
            setCreateOpen(false);
            setPinModal(disclosure);
            reload();
          }}
        />
      ) : null}

      {pinModal ? <TempPinDialog disclosure={pinModal} onClose={() => setPinModal(null)} /> : null}
      {bulkPins ? <BulkPinDialog results={bulkPins} onClose={() => setBulkPins(null)} /> : null}
    </div>
  );
}

function BulkPinDialog({ results, onClose }: { results: BulkOutcome[]; onClose: () => void }) {
  const withPins = results.filter((r) => r.tempPin);

  function copyAll() {
    const text = withPins.map((r) => `${r.userId.slice(0, 8)}: ${r.tempPin}`).join('\n');
    void navigator.clipboard.writeText(text).catch(() => undefined);
  }

  function printAll() {
    const html = `<!doctype html><html><head><title>Bulk PIN reset</title>
<style>body{font-family:ui-sans-serif,system-ui;padding:24px}
.card{margin-bottom:16px;border:1px solid #000;border-radius:8px;padding:12px}
.pin{font-family:ui-monospace,monospace;font-size:32px;letter-spacing:6px}</style>
</head><body>
<h2>Qargo IMS — Bulk PIN reset</h2>
${withPins
  .map(
    (r) => `<div class="card"><div>User: <code>${r.userId}</code></div>
<div class="pin">${r.tempPin ?? ''}</div></div>`,
  )
  .join('')}
<p style="font-size:12px;color:#555">Each user is forced to set a new PIN on next login.</p>
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.print();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl p-6 w-full max-w-lg my-8">
        <h3 className="text-lg font-semibold">Bulk PIN reset</h3>
        <p className="text-xs text-muted-foreground mt-1">
          {withPins.length} temp PIN{withPins.length === 1 ? '' : 's'} issued. Shown exactly once —
          copy or print before closing.
        </p>
        <ul className="my-4 space-y-2 max-h-72 overflow-y-auto">
          {withPins.map((r) => (
            <li
              key={r.userId}
              className="flex items-center justify-between border border-border rounded-md px-3 py-2"
            >
              <code className="text-xs text-muted-foreground">{r.userId.slice(0, 12)}…</code>
              <code className="font-mono text-lg tracking-widest">{r.tempPin}</code>
            </li>
          ))}
          {results
            .filter((r) => r.status === 'skipped')
            .map((r) => (
              <li key={r.userId} className="text-xs text-muted-foreground italic">
                {r.userId.slice(0, 12)}… — skipped ({r.reason ?? 'unknown'})
              </li>
            ))}
        </ul>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={copyAll}
            className="px-3 py-1 rounded-md border border-border text-sm"
          >
            Copy all
          </button>
          <button
            type="button"
            onClick={printAll}
            className="px-3 py-1 rounded-md border border-border text-sm"
          >
            Printable card
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (d: PinDisclosure) => void;
}) {
  const { scopedStores } = useUserContext();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [homeStoreId, setHomeStoreId] = useState<string>(scopedStores[0] ?? '');
  const [role, setRole] = useState<'barista' | 'shift_lead'>('barista');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!username || !homeStoreId) {
      setErr('username and home store are required');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const resp = await apiFetch<CreateResponse>('/users', {
        method: 'POST',
        body: JSON.stringify({
          username,
          displayName: displayName || undefined,
          homeStoreId,
          role,
        }),
      });
      onCreated({
        username: resp.user.username,
        storeCode: resp.user.homeStoreCode,
        tempPin: resp.tempPin,
        kind: 'create',
      });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'create failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md space-y-3">
        <h3 className="text-lg font-semibold">Create user</h3>
        <label className="block text-sm">
          Username
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1"
            placeholder="ada.lovelace"
            autoFocus
          />
        </label>
        <label className="block text-sm">
          Display name
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1"
            placeholder="Ada Lovelace"
          />
        </label>
        <label className="block text-sm">
          Home store
          <input
            value={homeStoreId}
            onChange={(e) => setHomeStoreId(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1 font-mono text-xs"
            placeholder="store UUID"
          />
          {scopedStores.length > 0 ? (
            <p className="text-xs text-muted-foreground mt-1">
              Your scope: {scopedStores.slice(0, 3).join(', ')}
              {scopedStores.length > 3 ? `, +${scopedStores.length - 3} more` : ''}
            </p>
          ) : null}
        </label>
        <label className="block text-sm">
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'barista' | 'shift_lead')}
            className="mt-1 w-full border border-border rounded-md px-2 py-1"
          >
            <option value="barista">barista</option>
            <option value="shift_lead">shift lead</option>
          </select>
        </label>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
          >
            {submitting ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TempPinDialog({
  disclosure,
  onClose,
}: {
  disclosure: PinDisclosure;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copyPin() {
    try {
      await navigator.clipboard.writeText(disclosure.tempPin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // some browsers refuse clipboard in non-secure contexts; user can still read it.
    }
  }

  function printCard() {
    const html = `<!doctype html><html><head><title>Temporary PIN — ${disclosure.username}</title>
<style>body{font-family:ui-sans-serif,system-ui;padding:48px;color:#373643}
.card{max-width:480px;border:2px solid #003465;border-radius:12px;padding:24px}
.brand{color:#003465;font-size:12px;font-weight:700;letter-spacing:4px;text-transform:uppercase;margin:0 0 4px}
h2{color:#003465;margin-top:0}
.pin{font-family:ui-monospace,monospace;font-size:48px;letter-spacing:8px;margin:16px 0;color:#003465}
.muted{color:#555;font-size:12px;margin-top:24px}
.footer{color:#2F5972;font-size:11px;letter-spacing:2px;margin-top:16px}</style></head>
<body><div class="card">
<p class="brand">Qargo Coffee</p>
<h2>Qargo IMS · Temporary PIN</h2>
<p><strong>Store:</strong> ${disclosure.storeCode}</p>
<p><strong>Username:</strong> ${disclosure.username}</p>
<div class="pin">${disclosure.tempPin}</div>
<p>You will be required to set a new 6-digit PIN on your first login.</p>
<p class="muted">Hand this to the user in person. This PIN is shown exactly once and will not be retrievable again.</p>
<p class="footer">TMKN · Qargo Coffee</p>
</div></body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.print();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold">Temporary PIN for {disclosure.username}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {disclosure.kind === 'create' ? 'New user created' : 'PIN reset'} at store{' '}
          <code>{disclosure.storeCode}</code>. This PIN is shown exactly once.
        </p>
        <div className="my-6 text-center">
          <div className="font-mono text-5xl tracking-widest select-all">{disclosure.tempPin}</div>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          The user must set a new PIN on first login. Hand them this PIN in person.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void copyPin()}
            className="px-3 py-1 rounded-md border border-border text-sm"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            type="button"
            onClick={printCard}
            className="px-3 py-1 rounded-md border border-border text-sm"
          >
            Printable card
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
